import { Fragment } from "react";

import { cn } from "@/lib/utils";
import { Kbd } from "./kbd";

const MAC_MODIFIERS: Record<string, string> = { $mod: "⌘", ctrl: "⌃", alt: "⌥", option: "⌥", shift: "⇧" };
const OTHER_MODIFIERS: Record<string, string> = { $mod: "Ctrl", ctrl: "Ctrl", alt: "Alt", option: "Alt", shift: "Shift" };
const NAMED_KEYS: Record<string, string> = {
  space: "Space",
  enter: "Enter",
  escape: "Esc",
  backspace: "Backspace",
  delete: "Delete",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
};

function isMac() {
  return typeof navigator !== "undefined" && navigator.platform.toLowerCase().includes("mac");
}

/** One chord as a key cap: "⌘/" on a Mac, as its menus write it, and "Ctrl+/" elsewhere. */
function chordLabel(chord: string, mac: boolean) {
  const keys = chord.split("+").map((key) => key.trim().toLowerCase()).filter(Boolean);
  const modifiers = mac ? MAC_MODIFIERS : OTHER_MODIFIERS;
  const labels = keys.map((key) => modifiers[key] ?? NAMED_KEYS[key] ?? key.toUpperCase());
  return labels.join(mac ? "" : "+");
}

/**
 * Shortcuts in the app's own notation, as key caps. Keys pressed in turn are
 * spaced ("g t" is G, then T), keys held together are joined with "+"
 * ("$mod+/"), and each alternative reads "or".
 */
export function ShortcutKeys({ shortcuts, className }: { shortcuts: readonly string[]; className?: string }) {
  const mac = isMac();
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground", className)}>
      {shortcuts.map((shortcut, index) => (
        <Fragment key={shortcut}>
          {index > 0 ? <span className="px-0.5">or</span> : null}
          {shortcut.trim().split(/\s+/u).map((chord, step) => (
            <Kbd key={`${chord}-${step}`}>{chordLabel(chord, mac)}</Kbd>
          ))}
        </Fragment>
      ))}
    </span>
  );
}
